// pages/admin/NowPaymentsManagement.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, RefreshCw, Eye, CheckCircle, XCircle,
  Clock, AlertCircle, DollarSign, User, Bitcoin,
  Calendar, Filter, ExternalLink as LinkIcon,
  ChevronLeft, ChevronRight, Shield, TrendingUp, FileText,
  Zap, Wallet
} from 'lucide-react';
import { AdminNowPaymentsAPI, AdminAPIUtils, AdminTokenService } from '../services/api';

const NowPaymentsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    user_id: ''
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'refresh', 'cancel', 'credit'
  const [notes, setNotes] = useState('');
  const [creditAmount, setCreditAmount] = useState('');

  useEffect(() => {
    fetchStats();
    fetchInvoices();
  }, [pagination.page, filters.status]);

  const fetchStats = async () => {
    try {
      const response = await AdminNowPaymentsAPI.getStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filters.status !== 'all') {
        params.status = filters.status;
      }
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.user_id) {
        params.user_id = filters.user_id;
      }

      const response = await AdminNowPaymentsAPI.getInvoices(params);
      
      if (response.success) {
        setInvoices(response.invoices || []);
        setPagination({
          page: response.pagination?.page || 1,
          limit: response.pagination?.limit || 20,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.pages || 1
        });
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshInvoice = async (invoiceId) => {
    setSelectedInvoice(invoices.find(i => i.invoice_id === invoiceId));
    setModalType('refresh');
    setShowModal(true);
  };

  const handleCancelInvoice = async (invoiceId) => {
    setSelectedInvoice(invoices.find(i => i.invoice_id === invoiceId));
    setModalType('cancel');
    setShowModal(true);
  };

  const handleManualCredit = async (invoiceId) => {
    setSelectedInvoice(invoices.find(i => i.invoice_id === invoiceId));
    setModalType('credit');
    setCreditAmount(selectedInvoice?.pay_amount || '');
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (!selectedInvoice) return;
    
    setProcessing(true);
    
    try {
      let response;
      
      if (modalType === 'refresh') {
        response = await AdminNowPaymentsAPI.refreshInvoice(selectedInvoice.invoice_id);
        if (response.success) {
          alert(`✅ Invoice status updated: ${response.old_status} → ${response.new_status}`);
        }
      } else if (modalType === 'cancel') {
        response = await AdminNowPaymentsAPI.cancelInvoice(selectedInvoice.invoice_id, notes);
        if (response.success) {
          alert('✅ Invoice cancelled successfully');
        }
      } else if (modalType === 'credit') {
        response = await AdminNowPaymentsAPI.manualCredit({
          invoice_id: selectedInvoice.invoice_id,
          user_id: selectedInvoice.user_id,
          amount: parseFloat(creditAmount),
          notes: notes
        });
        if (response.success) {
          alert(`✅ User credited with ${creditAmount} BTC`);
        }
      }
      
      // Refresh data
      await fetchInvoices();
      await fetchStats();
      
      setShowModal(false);
      setNotes('');
      setCreditAmount('');
      setSelectedInvoice(null);
      setModalType('');
      
    } catch (err) {
      console.error('Failed to process:', err);
      alert('❌ Failed to process. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      waiting: { 
        bg: 'bg-yellow-500/10', 
        text: 'text-yellow-400', 
        border: 'border-yellow-500/20',
        icon: <Clock className="w-3 h-3" />,
        label: 'Waiting'
      },
      confirming: { 
        bg: 'bg-blue-500/10', 
        text: 'text-blue-400', 
        border: 'border-blue-500/20',
        icon: <RefreshCw className="w-3 h-3 animate-spin" />,
        label: 'Confirming'
      },
      partially_paid: { 
        bg: 'bg-orange-500/10', 
        text: 'text-orange-400', 
        border: 'border-orange-500/20',
        icon: <AlertCircle className="w-3 h-3" />,
        label: 'Partial'
      },
      confirmed: { 
        bg: 'bg-emerald-500/10', 
        text: 'text-emerald-400', 
        border: 'border-emerald-500/20',
        icon: <CheckCircle className="w-3 h-3" />,
        label: 'Confirmed'
      },
      finished: { 
        bg: 'bg-green-500/10', 
        text: 'text-green-400', 
        border: 'border-green-500/20',
        icon: <CheckCircle className="w-3 h-3" />,
        label: '✅ Finished'
      },
      expired: { 
        bg: 'bg-gray-500/10', 
        text: 'text-gray-400', 
        border: 'border-gray-500/20',
        icon: <AlertCircle className="w-3 h-3" />,
        label: 'Expired'
      },
      failed: { 
        bg: 'bg-rose-500/10', 
        text: 'text-rose-400', 
        border: 'border-rose-500/20',
        icon: <XCircle className="w-3 h-3" />,
        label: 'Failed'
      },
      refunded: { 
        bg: 'bg-purple-500/10', 
        text: 'text-purple-400', 
        border: 'border-purple-500/20',
        icon: <RefreshCw className="w-3 h-3" />,
        label: 'Refunded'
      },
      manually_credited: { 
        bg: 'bg-cyan-500/10', 
        text: 'text-cyan-400', 
        border: 'border-cyan-500/20',
        icon: <Zap className="w-3 h-3" />,
        label: 'Manual Credit'
      }
    };
    
    const configItem = config[status] || config.waiting;
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusActions = (invoice) => {
    const actions = [];
    
    // Refresh button - always available
    actions.push({
      label: 'Refresh',
      icon: <RefreshCw className="w-4 h-4" />,
      onClick: () => handleRefreshInvoice(invoice.invoice_id),
      color: 'text-blue-400 hover:text-blue-300'
    });
    
    // Cancel button - only for pending invoices
    if (['waiting', 'confirming', 'partially_paid'].includes(invoice.status)) {
      actions.push({
        label: 'Cancel',
        icon: <XCircle className="w-4 h-4" />,
        onClick: () => handleCancelInvoice(invoice.invoice_id),
        color: 'text-rose-400 hover:text-rose-300'
      });
    }
    
    // Manual credit - only for failed/expired invoices
    if (['failed', 'expired'].includes(invoice.status)) {
      actions.push({
        label: 'Credit User',
        icon: <Wallet className="w-4 h-4" />,
        onClick: () => handleManualCredit(invoice.invoice_id),
        color: 'text-emerald-400 hover:text-emerald-300'
      });
    }
    
    // View details - always available
    actions.push({
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: () => navigate(`/admin/nowpayments/invoice/${invoice.invoice_id}`),
      color: 'text-gray-400 hover:text-white'
    });
    
    return actions;
  };

  const statCards = [
    {
      title: 'Total Invoices',
      value: stats?.total || 0,
      icon: <FileText className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Success Rate',
      value: stats?.success_rate || 0 + '%',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'Successful',
      value: stats?.successful || 0,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Failed',
      value: stats?.failed || 0,
      icon: <XCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-600'
    },
    {
      title: 'Total Volume',
      value: AdminAPIUtils.formatBTC(stats?.total_volume || 0) + ' BTC',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-purple-500 to-pink-600'
    },
    {
      title: 'Pending',
      value: (() => {
        const pending = stats?.by_status?.filter(s => 
          ['waiting', 'confirming', 'partially_paid'].includes(s.status)
        );
        return pending?.reduce((sum, s) => sum + s.count, 0) || 0;
      })(),
      icon: <Clock className="w-5 h-5" />,
      color: 'from-yellow-500 to-amber-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
            ⚡ NOWPayments Invoices
          </h1>
          <p className="text-charcoal-gray mt-1">Manage and monitor crypto deposit invoices</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchInvoices}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-4 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
            </div>
            <div>
              <p className="text-xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-xs uppercase tracking-wider">{card.title}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Search by invoice ID, user, email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && fetchInvoices()}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all duration-300"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <Filter className="text-charcoal-gray w-5 h-5" />
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="waiting">Waiting</option>
                <option value="confirming">Confirming</option>
                <option value="partially_paid">Partial</option>
                <option value="confirmed">Confirmed</option>
                <option value="finished">Finished</option>
                <option value="expired">Expired</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="manually_credited">Manual Credit</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3">
              <User className="text-charcoal-gray w-5 h-5" />
              <input
                type="text"
                placeholder="User ID"
                value={filters.user_id}
                onChange={(e) => setFilters(prev => ({ ...prev, user_id: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && fetchInvoices()}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all duration-300"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4 pt-4 border-t border-gray-800">
          <button
            onClick={() => {
              setFilters({ status: 'all', search: '', user_id: '' });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm text-charcoal-gray hover:text-white transition-colors hover:bg-gray-800/50 px-3 py-1.5 rounded-lg"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading invoices...</p>
            </div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <Zap className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No invoices found</h3>
            <p className="text-charcoal-gray mb-6">Try adjusting your filters</p>
            <button
              onClick={() => {
                setFilters({ status: 'all', search: '', user_id: '' });
                fetchInvoices();
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white rounded-xl transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Invoice
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Bitcoin className="w-4 h-4" />
                        Currency
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Created
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {invoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className="hover:bg-gray-800/30 transition-colors group"
                    >
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-white font-mono">
                            {invoice.invoice_id.substring(0, 16)}...
                          </span>
                          <span className="text-xs text-charcoal-gray">
                            {invoice.order_id || 'No order ID'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                            {invoice.username?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white">{invoice.username}</div>
                            <div className="text-xs text-charcoal-gray truncate max-w-[140px]">{invoice.email}</div>
                            <div className="text-xs text-gray-500">ID: {invoice.user_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-emerald-400">
                            {AdminAPIUtils.formatBTC(invoice.pay_amount)} 
                          </span>
                          {invoice.paid_amount && (
                            <span className="text-xs text-charcoal-gray">
                              Paid: {AdminAPIUtils.formatBTC(invoice.paid_amount)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-white uppercase">
                          {invoice.pay_currency || 'BTC'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{formatDate(invoice.created_at)}</div>
                        {invoice.completed_at && (
                          <div className="text-xs text-emerald-400">
                            Paid: {formatDate(invoice.completed_at)}
                          </div>
                        )}
                        {invoice.expired_at && (
                          <div className="text-xs text-rose-400">
                            Expired: {formatDate(invoice.expired_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice.status)}
                        {invoice.confirmations > 0 && (
                          <div className="text-xs text-charcoal-gray mt-1">
                            {invoice.confirmations} confirmations
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          {getStatusActions(invoice).map((action, idx) => (
                            <button
                              key={idx}
                              onClick={action.onClick}
                              className={`p-2 bg-gray-800 hover:bg-gray-700 ${action.color} rounded-lg transition-all duration-300 hover:scale-110 group relative`}
                              title={action.label}
                            >
                              {action.icon}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-night-blue border border-gray-800 rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                {modalType === 'refresh' && '🔄 Refresh Invoice'}
                {modalType === 'cancel' && '❌ Cancel Invoice'}
                {modalType === 'credit' && '💰 Manual Credit'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setNotes('');
                  setCreditAmount('');
                  setSelectedInvoice(null);
                  setModalType('');
                }}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-gray-800/30 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-charcoal-gray">Invoice</span>
                  <span className="text-white font-mono text-sm">{selectedInvoice.invoice_id}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-charcoal-gray">User</span>
                  <span className="text-white font-medium">{selectedInvoice.username}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-charcoal-gray">Amount</span>
                  <span className="text-emerald-400 font-bold">
                    {AdminAPIUtils.formatBTC(selectedInvoice.pay_amount)} {selectedInvoice.pay_currency}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-charcoal-gray">Status</span>
                  <span className="text-white">{selectedInvoice.status}</span>
                </div>
              </div>

              {modalType === 'cancel' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Cancellation Reason *
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Why is this invoice being cancelled?"
                    className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none"
                    rows="3"
                    required
                  />
                </div>
              )}

              {modalType === 'credit' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Amount to Credit (BTC) *
                    </label>
                    <input
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      step="0.00000001"
                      min="0"
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Reason for manual credit..."
                      className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors resize-none"
                      rows="2"
                    />
                  </div>
                </>
              )}

              {modalType === 'refresh' && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-blue-400 text-sm">
                    ⚡ This will check the current status from NOWPayments API and update the local record.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNotes('');
                  setCreditAmount('');
                  setSelectedInvoice(null);
                  setModalType('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={processing || (modalType === 'cancel' && !notes) || (modalType === 'credit' && !creditAmount)}
                className={`flex-1 px-4 py-2.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                  modalType === 'refresh' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white'
                    : modalType === 'cancel'
                    ? 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white'
                    : 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {modalType === 'refresh' && <RefreshCw className="w-4 h-4" />}
                    {modalType === 'cancel' && <XCircle className="w-4 h-4" />}
                    {modalType === 'credit' && <CheckCircle className="w-4 h-4" />}
                    {modalType === 'refresh' && 'Refresh'}
                    {modalType === 'cancel' && 'Cancel Invoice'}
                    {modalType === 'credit' && 'Credit User'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NowPaymentsManagement;